import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, parseMoney, sumarDias, type Settings } from '@abastece/shared';
import type { Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cambioBp, type DineroDeAnalisis, type Tramo } from '../lib/analisis.js';
import { envioDelPeriodo, porCanal, porListaDePrecios, type Canal } from '../lib/canales.js';
import { etiquetaListaPrecio } from '../lib/catalogo.js';
import { emitir } from '../lib/emitir.js';
import { otroMes } from '../lib/historial.js';
import { ventasDelPeriodo } from '../lib/libros.js';
import {
  DIAS_DEL_MES_ATRAS,
  SEMANAS_DE_RITMO,
  avanceDeMeta,
  diasFuertes,
  porDiaDelMes,
  proyeccionDelMes,
  type AvanceMeta,
} from '../lib/proyeccion.js';
import { usePuede } from '../lib/sesion.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { Campo } from './Campo.js';
import { Barras, Cambio, Columnas } from './Graficas.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { Nada, Seccion, parte, pct, useFechas } from './PiezasDeAnalisis.js';

/**
 * What «Ventas» in «Análisis» says besides its figures (the owner, 2026-10-13: «let's build all of
 * it»): where the month will close and how far along its goals are, the counter against the orders
 * and the deliveries, each price list, and which days of the month sell the most. The arithmetic is
 * `lib/proyeccion.ts` and `lib/canales.ts`.
 */

/** Each channel's name: spelled out, so `pnpm i18n:check` sees the keys are used. */
const CANAL: Record<Canal, Clave> = { mostrador: 'analisis.canal.mostrador', pedido: 'analisis.canal.pedido', envio: 'analisis.canal.envio' };

/**
 * «Cómo va el mes», on this month only: where it closes at the pace the shop goes, against the
 * whole of last month and the same month a year before, and the month's goals with what each day
 * left has to bring. The owner sets the goals here, in a sheet.
 */
export function ComoVaElMes({ hoyEs, dinero }: { hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const puedeMetas = usePuede('configurar');
  const [metas, setMetas] = useState(false);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const mes = hoyEs.slice(0, 7);
  const anioAntes = `${Number(mes.slice(0, 4)) - 1}${mes.slice(4)}`;
  const proy = useLiveQuery(async () => {
    const ritmo = sumarDias(hoyEs, -7 * SEMANAS_DE_RITMO);
    const pasado = `${otroMes(mes, -1)}-01`;
    const [recientes, antes] = await Promise.all([
      ventasDelPeriodo({ desde: ritmo < pasado ? ritmo : pasado, hasta: hoyEs }),
      ventasDelPeriodo({ desde: `${anioAntes}-01`, hasta: `${anioAntes}-31` }),
    ]);
    return proyeccionDelMes([...recientes, ...antes], hoyEs);
  }, [hoyEs]);
  if (!proy) return null;

  const quedan = proy.dias - proy.transcurridos + 1;
  const ventas = config.metaVentasMes ? avanceDeMeta(config.metaVentasMes, proy.vendido, proy.proyectado, quedan) : null;
  const utilidad = config.metaUtilidadMes ? avanceDeMeta(config.metaUtilidadMes, proy.utilidad, proy.utilidadProyectada, quedan) : null;

  return (
    <Seccion
      titulo={t('analisis.mes.titulo')}
      ayuda={t(proy.segun === 'semana' ? 'analisis.mes.ayudaSemana' : 'analisis.mes.ayudaRitmo', { n: SEMANAS_DE_RITMO })}
      datos="como-va"
      accion={
        puedeMetas ? (
          <button type="button" className="btn-secundario min-h-10 gap-1.5 px-3 text-sm" onClick={() => setMetas(true)} data-poner-metas>
            <Ico icono="editar" className="size-4" />
            {t('analisis.metas.boton')}
          </button>
        ) : undefined
      }
    >
      <p className="text-base font-medium" data-proyeccion>
        {t('analisis.mes.cierra', { monto: dinero(proy.proyectado) })}
      </p>
      <p className="text-sm text-slate-400" data-proyeccion-utilidad>
        {t('analisis.mes.deja', { monto: dinero(proy.utilidadProyectada) })}
      </p>
      <p className="mt-1 text-xs text-slate-500">{t('analisis.mes.van', { n: proy.transcurridos, de: proy.dias, monto: dinero(proy.vendido) })}</p>
      {(proy.mesPasado || proy.haceUnAnio) && (
        <ul className="mt-3 flex flex-col gap-1 text-sm text-slate-300">
          {proy.mesPasado && (
            <li className="flex flex-wrap items-baseline gap-x-2" data-mes-pasado>
              {t('analisis.mes.pasado', { monto: dinero(proy.mesPasado.ingreso) })}
              <Cambio bp={cambioBp(proy.proyectado, proy.mesPasado.ingreso)} />
            </li>
          )}
          {proy.haceUnAnio && (
            <li className="flex flex-wrap items-baseline gap-x-2" data-hace-un-anio>
              {t('analisis.mes.anioPasado', { mes: f.mesLargo(`${anioAntes}-01`), monto: dinero(proy.haceUnAnio.ingreso) })}
              <Cambio bp={cambioBp(proy.proyectado, proy.haceUnAnio.ingreso)} />
            </li>
          )}
        </ul>
      )}
      {(ventas || utilidad) && (
        <div className="mt-4 flex flex-col gap-3">
          {ventas && <BarraDeMeta nombre={t('analisis.metas.ventas')} avance={ventas} dinero={dinero} datos="ventas" />}
          {utilidad && <BarraDeMeta nombre={t('analisis.metas.utilidad')} avance={utilidad} dinero={dinero} datos="utilidad" />}
        </div>
      )}
      {!ventas && !utilidad && puedeMetas && <p className="mt-3 text-xs text-slate-500">{t('analisis.metas.sinMetas')}</p>}
      {metas && <MetasDelMes config={config} onCerrar={() => setMetas(false)} />}
    </Seccion>
  );
}

function BarraDeMeta({ nombre, avance, dinero, datos }: { nombre: string; avance: AvanceMeta; dinero: DineroDeAnalisis; datos: string }) {
  const t = useT();
  const bien = avance.llega || avance.faltaAlDia === null;
  return (
    <div data-meta={datos}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span>{nombre}</span>
        <span className="tabular text-slate-300">{t('analisis.metas.deMeta', { llevado: dinero(avance.llevado), meta: dinero(avance.meta) })}</span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-800" aria-hidden>
        <div className={`h-full rounded-full ${bien ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${Math.min(100, avance.parteBp / 100)}%` }} />
      </div>
      <p className={`mt-1 text-xs ${bien ? 'text-emerald-300' : 'text-amber-300'}`} data-meta-dice>
        {avance.faltaAlDia === null
          ? t('analisis.metas.cumplida')
          : avance.llega
            ? t('analisis.metas.llega')
            : t('analisis.metas.falta', { monto: dinero(avance.faltaAlDia) })}
      </p>
    </div>
  );
}

/** A goal as typed: empty is none, and anything else has to be an amount above nothing. */
function leerMeta(texto: string): number | null | undefined {
  if (texto.trim() === '') return null;
  const c = parseMoney(texto);
  return c !== null && c > 0 ? c : undefined;
}
const comoTexto = (c: number | null): string => (c ? (c / 100).toFixed(2) : '');

/** «Metas»: what each month should sell and leave, in córdobas, a setting like any other of the owner's. */
function MetasDelMes({ config, onCerrar }: { config: Settings; onCerrar: () => void }) {
  const t = useT();
  const [ventas, setVentas] = useState(comoTexto(config.metaVentasMes));
  const [utilidad, setUtilidad] = useState(comoTexto(config.metaUtilidadMes));
  const [trabajando, setTrabajando] = useState(false);
  const v = leerMeta(ventas);
  const u = leerMeta(utilidad);
  async function guardar() {
    if (v === undefined || u === undefined) return;
    setTrabajando(true);
    try {
      if (v !== config.metaVentasMes) await emitir('config.cambiada', { clave: 'metaVentasMes', valor: v, anterior: config.metaVentasMes });
      if (u !== config.metaUtilidadMes) await emitir('config.cambiada', { clave: 'metaUtilidadMes', valor: u, anterior: config.metaUtilidadMes });
      onCerrar();
    } finally {
      setTrabajando(false);
    }
  }
  return (
    <Modal
      titulo={t('analisis.metas.titulo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={trabajando || v === undefined || u === undefined} onClick={() => void guardar()} data-metas-guardar>
          <Ico icono="guardar" />
          {t('comun.guardar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4" data-metas>
        <p className="text-sm text-slate-400">{t('analisis.metas.ayuda')}</p>
        <Campo
          etiqueta={t('analisis.metas.ventas')}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.00"
          value={ventas}
          onChange={(e) => setVentas(e.target.value)}
          error={v === undefined ? t('analisis.metas.mal') : null}
          data-meta-ventas
        />
        <Campo
          etiqueta={t('analisis.metas.utilidad')}
          inputMode="decimal"
          autoComplete="off"
          placeholder="0.00"
          value={utilidad}
          onChange={(e) => setUtilidad(e.target.value)}
          error={u === undefined ? t('analisis.metas.mal') : null}
          data-meta-utilidad
        />
      </div>
    </Modal>
  );
}

/**
 * «Por día del mes»: what each day of the month sells on average over the last six months, a
 * column a day, and the days that sell the most: the 15th and the month's end are paydays.
 */
export function PorDiaDelMes({ hoyEs, dinero }: { hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const datos = useLiveQuery(async () => {
    const ayer = sumarDias(hoyEs, -1);
    const desde = sumarDias(hoyEs, -DIAS_DEL_MES_ATRAS);
    const dias = porDiaDelMes(await ventasDelPeriodo({ desde, hasta: ayer }), desde, ayer);
    return { dias, fuertes: diasFuertes(dias) };
  }, [hoyEs]);
  if (!datos) return null;
  const lista = new Intl.ListFormat(localeActual(), { style: 'long', type: 'conjunction' });
  return (
    <Seccion titulo={t('analisis.diaDelMes.titulo')} ayuda={t('analisis.diaDelMes.ayuda')} datos="dia-del-mes">
      {datos.fuertes === null ? (
        <Nada texto={t('analisis.sinVentas')} />
      ) : (
        <>
          <Columnas
            columnas={datos.dias.map((d) => ({
              clave: String(d.dia),
              etiqueta: String(d.dia),
              valor: d.promedio,
              parte: 0,
              texto: t('analisis.diaDelMes.columna', { dia: d.dia, monto: dinero(d.promedio) }),
            }))}
            resumen={t('analisis.diaDelMes.titulo')}
          />
          <p className="mt-3 text-sm text-slate-300" data-dias-fuertes>
            {t('analisis.diaDelMes.fuertes', { dias: lista.format(datos.fuertes.dias.map(String)), pct: pct(datos.fuertes.sobrePromedioBp) })}
          </p>
        </>
      )}
    </Seccion>
  );
}

/**
 * «Por dónde se vende»: what the counter, the orders and the orders brought to the door each sold
 * and left; and whether delivery pays for itself, what «Envío» charged against transport.
 */
export function PorCanal({ ventas, tramo, dinero }: { ventas: readonly Venta[]; tramo: Tramo; dinero: DineroDeAnalisis }) {
  const t = useT();
  const gastos = useLiveQuery(() => db.gastos.where('fecha').between(tramo.desde, tramo.hasta, true, true).toArray(), [tramo.desde, tramo.hasta], []);
  const filas = porCanal(ventas, tramo);
  const envio = envioDelPeriodo(ventas, gastos, tramo);
  const total = filas.reduce((a, f) => a + f.ingreso, 0);
  if (filas.length === 0) return null;
  const sobra = envio.cobrado - envio.transporte;
  return (
    <Seccion titulo={t('analisis.canal.titulo')} ayuda={t('analisis.canal.ayuda')} datos="canales">
      <Barras
        datos="canales"
        tono="bg-sky-500"
        filas={filas.map((f) => ({
          clave: f.canal,
          etiqueta: t(CANAL[f.canal]),
          valor: f.ingreso,
          texto: `${dinero(f.ingreso)} · ${pct(parte(f.ingreso, total))}`,
          detalle: t('analisis.canal.detalle', { n: f.ventas, utilidad: dinero(f.utilidad), margen: pct(f.margenBp), ticket: f.ticket === null ? '—' : dinero(f.ticket) }),
        }))}
      />
      {(envio.entregas > 0 || envio.transporte > 0) && (
        <div className="mt-4 flex flex-col gap-0.5 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300" data-envio-paga>
          <p>{t('analisis.canal.envioCobro', { monto: dinero(envio.cobrado), n: envio.entregas })}</p>
          <p>{t('analisis.canal.transporte', { categoria: etiquetaCategoriaGasto('transporte'), monto: dinero(envio.transporte) })}</p>
          <p className={`font-medium ${sobra >= 0 ? 'text-emerald-300' : 'text-amber-300'}`} data-envio-sobra={sobra >= 0 ? 'si' : 'no'}>
            {sobra >= 0 ? t('analisis.canal.envioSobra', { monto: dinero(sobra) }) : t('analisis.canal.envioFalta', { monto: dinero(-sobra) })}
          </p>
          {envio.promedioPedido !== null && <p className="text-xs text-slate-400">{t('analisis.canal.pedidoPromedio', { monto: dinero(envio.promedioPedido) })}</p>}
        </div>
      )}
    </Seccion>
  );
}

/** «Por lista de precios»: what sold at each price and what it left, when more than one was used. */
export function PorLista({ ventas, tramo, dinero }: { ventas: readonly Venta[]; tramo: Tramo; dinero: DineroDeAnalisis }) {
  const t = useT();
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const filas = porListaDePrecios(ventas, tramo);
  if (filas.length < 2) return null;
  const total = filas.reduce((a, f) => a + f.ingreso, 0);
  const nombre = (id: string) => {
    const l = listas.find((x) => x.id === id);
    return l ? etiquetaListaPrecio(l) : '—';
  };
  return (
    <Seccion titulo={t('analisis.lista.titulo')} ayuda={t('analisis.lista.ayuda')} datos="listas">
      <Barras
        datos="listas"
        tono="bg-violet-500"
        filas={filas.map((f) => ({
          clave: f.listaId,
          etiqueta: nombre(f.listaId),
          valor: f.ingreso,
          texto: `${dinero(f.ingreso)} · ${pct(parte(f.ingreso, total))}`,
          detalle: t('analisis.lista.detalle', { n: f.ventas, utilidad: dinero(f.utilidad), margen: pct(f.margenBp) }),
        }))}
      />
    </Seccion>
  );
}
